import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  Drawer,
  Tooltip,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Menu as MenuIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { selectCompanyInfo } from '../../../app/store/slices/companySlice';

import CategoryPanel from '../components/CategoryPanel';
import ProductGrid from '../components/ProductGrid';
import CartPanel from '../components/CartPanel';
import PreviewDialog from '../components/PreviewDialog';
import CustomerDialog from '../components/CustomerDialog';
import PrintOptionsDialog from '../components/PrintOptionsDialog';
import A4PreviewDialog from '../components/A4PreviewDialog';

import { searchProducts } from '../../product/services/productService';
import { A4InvoiceTemplate } from '../components/A4InvoiceTemplate';
import { getThermalReceiptTemplate } from '../components/ThermalReceiptTemplate';
import { getVatRates } from '../../companysettings/services/vatService'; // Adjust path based on your structure
import { createTicket, validateTicketData, transformCartToTicketDto } from '../services/ticketDeCaisseService'; // ✅ Import createTicket directly

function generateReceiptNumber() {
  const prefix = 'TKT';
  const timestamp = Date.now().toString().slice(-8);
  const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
  return `${prefix}-${timestamp}${random}`;
}

const PosListPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const posContainerRef = useRef(null);

  const companyInfo = useSelector(selectCompanyInfo);

  // State Management
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [cart, setCart] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [customVATRate, setCustomVATRate] = useState(21); // ✅ Changed from 0 to 21
  const [vatRates, setVatRates] = useState([]);
  
  // Dialog States
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [categoryDrawerOpen, setCategoryDrawerOpen] = useState(false);

  // Payment State
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('CASH');
  const [paidAmount, setPaidAmount] = useState('');
  const [receiptNumber, setReceiptNumber] = useState(generateReceiptNumber());

  // Fullscreen State
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [printOptionsOpen, setPrintOptionsOpen] = useState(false); // ✅ Add this state
  const [previewHtml, setPreviewHtml] = useState(''); // ✅ Add this state
  const [currentFormat, setCurrentFormat] = useState('receipt'); // ✅ Add this state

  // Add discount state:
   const [discount, setDiscount] = useState(null);

  // ✅ Log company info when it changes
  useEffect(() => {
    if (companyInfo) {
      console.log('🏢 Company info available in POS:', companyInfo);
    } else {
      console.warn('⚠️ No company info available in Redux store');
    }
  }, [companyInfo]);

  useEffect(() => {
    fetchProducts();

    // Add fullscreen change event listener
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );
      setIsFullscreen(isCurrentlyFullscreen);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  // ✅ Update the fetchProducts function to set "All Products" as default
  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await searchProducts({ name: '' });
      
      let productsData = [];
      if (response?.payload) {
        productsData = Array.isArray(response.payload) ? response.payload : [];
      } else if (response?.data) {
        productsData = Array.isArray(response.data) ? response.data : [];
      } else if (Array.isArray(response)) {
        productsData = response;
      }

      console.log('📦 Fetched products:', productsData.length);
      setProducts(productsData);
      setFilteredProducts(productsData); // ✅ Show all products initially
      
      // ✅ Extract categories after products are loaded
      if (productsData.length > 0) {
        extractCategoriesAndSetDefault(productsData);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error(t('failed_to_load_products') || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  // ✅ New function to extract categories and set "All Products" as default
  const extractCategoriesAndSetDefault = (productsArray) => {
    console.log('📦 Total Products:', productsArray.length);
    
    const categoryMap = new Map();
    
    // Extract unique categories with their product counts
    productsArray.forEach(product => {
      const categoryId = product.ProductCategoryId;
      const categoryName = product.ProductCategory?.CategoryName;
      
      if (categoryId && categoryName) {
        const trimmedName = categoryName.trim();
        if (!categoryMap.has(categoryId)) {
          categoryMap.set(categoryId, {
            id: categoryId,
            name: trimmedName,
            count: 0
          });
        }
        categoryMap.get(categoryId).count++;
      }
    });

    // Create "All Products" category
    const allProductsCategory = { 
      id: 'all', 
      name: t('all_products') || 'All Products', 
      icon: '🛍️',
      count: productsArray.length 
    };

    // Convert map to array and add icons
    const otherCategories = Array.from(categoryMap.values()).map(cat => ({
      ...cat,
      icon: getCategoryIcon(cat.name)
    }));

    const categoriesArray = [allProductsCategory, ...otherCategories];
    setCategories(categoriesArray);
    
    // ✅ Set "All Products" as default selected category
    setSelectedCategory(allProductsCategory);
    
    console.log('✅ Categories extracted, "All Products" set as default');
  };

  const getCategoryIcon = (categoryName) => {
    const iconMap = {
      'GENERAL': '📦',
      'PC': '💻',
      'CLAVIERS': '⌨️',
      'ECRANS': '🖥️',
      'MEMOIRES': '💾',
      'DISQUE DUR': '💿',
      'SACS/HOUSSES': '💼',
      'ACCESSOIRES': '🔌',
      'ADAPTATEURS': '🔌',
      'RESEAU': '🌐',
      'BOULANGERIE': '🥖',
      'FRUITS': '🍎',
      'ELECTRONICS': '📱',
      'CLOTHING': '👕',
      'FOOD': '🍔',
      'DRINKS': '🥤',
      'BOOKS': '📚',
    };

    const upperCat = (categoryName || '').toUpperCase();
    for (const [key, icon] of Object.entries(iconMap)) {
      if (upperCat.includes(key)) {
        return icon;
      }
    }
    return '📦';
  };

  // ✅ Update the filterProducts function:
  const filterProducts = () => {
    // Don't filter if search is active
    if (searchQuery.trim()) {
      return;
    }

    let filtered = [...products];

    console.log('🔍 Filter - Selected Category:', selectedCategory);
    console.log('🔍 Filter - Total Products:', products.length);

    // Apply category filter
    if (selectedCategory && selectedCategory.id !== 'all') {
      filtered = filtered.filter(p => {
        const productCategoryId = p.ProductCategoryId;
        return productCategoryId === selectedCategory.id;
      });
      console.log('📊 Filtered by category:', filtered.length, 'products');
    }

    console.log('✅ Final Filtered Products:', filtered.length);
    setFilteredProducts(filtered);
  };

  // ✅ Update addToCart - Use 21% VAT by default and calculate base price
  const addToCart = (product, quantity = 1) => {
    const existingIndex = cart.findIndex(item => item.Id === product.Id);

    // ✅ Calculate base price (price excluding VAT)
    const priceInclVAT = product.SalePrice || product.Price || 0;
    const vatRate = customVATRate !== null ? customVATRate : 21;
    const basePrice = priceInclVAT / (1 + vatRate / 100);

    if (existingIndex !== -1) {
      const updatedCart = [...cart];
      updatedCart[existingIndex].Quantity += quantity;
      setCart(updatedCart);
      toast.success(`${product.Name} ${t('quantity_updated') || 'quantity updated'}`);
    } else {
      setCart([
        ...cart,
        {
          ...product,
          Quantity: quantity,
          Price: basePrice, // ✅ Store base price (excluding VAT)
          PriceInclVAT: priceInclVAT, // ✅ Store original price for reference
          VAT: vatRate, // ✅ Default to 21% or customVATRate
          _rowKey: Date.now() + Math.random(),
        },
      ]);
      toast.success(`${product.Name} ${t('added_to_cart') || 'added to cart'}`);
    }
  };

  const updateCartQuantity = (rowKey, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(rowKey);
      return;
    }

    setCart(cart.map(item =>
      item._rowKey === rowKey
        ? { ...item, Quantity: newQuantity }
        : item
    ));
  };

  const removeFromCart = (rowKey) => {
    setCart(cart.filter(item => item._rowKey !== rowKey));
    toast.info(t('item_removed') || 'Item removed');
  };

  const clearCart = () => {
    setCart([]);
    setCustomer(null);
    setPaidAmount('');
    setDiscount(null);
    setCustomVATRate(21); // ✅ Reset to 21% instead of 0
    setReceiptNumber(generateReceiptNumber());
    toast.info(t('cart_cleared') || 'Cart cleared');
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setSearchQuery(''); // Clear search when changing category
    if (isMobile) {
      setCategoryDrawerOpen(false);
    }
  };

  const handlePreview = () => {
    if (cart.length === 0) {
      toast.error(t('please_add_products') || 'Please add at least one product');
      return;
    }
    
    console.log('🔍 Preview - Company Info at Preview Time:', companyInfo);
    
    setPrintOptionsOpen(true); // ✅ Open print options dialog instead
  };

  const handlePrint = () => {
    if (cart.length === 0) {
      toast.error(t('please_add_products') || 'Please add at least one product');
      return;
    }
    
    setPrintOptionsOpen(true); // ✅ Open print options dialog instead
  };

  // ✅ Update handleFormatSelection to handle both preview and print correctly
  const handleFormatSelection = async (format, action) => {
    setPrintOptionsOpen(false);
    setCurrentFormat(format);

    console.log(`📄 ${action === 'preview' ? 'Preview' : 'Print'} - Format: ${format}`);
    console.log('🏢 Company Info:', companyInfo);
    console.log('💰 Discount:', discount);
    console.log('📊 Custom VAT Rate:', customVATRate);

    let templateHtml;

    if (format === 'a4') {
      templateHtml = A4InvoiceTemplate(
        cart,
        customer,
        companyInfo,
        selectedPaymentMethod,
        paidAmount,
        receiptNumber,
        discount,
        customVATRate
      );
    } else {
      templateHtml = getThermalReceiptTemplate(
        cart,
        customer,
        companyInfo,
        selectedPaymentMethod,
        paidAmount,
        receiptNumber,
        discount,
        customVATRate
      );
    }

    console.log('📄 Generated Template (first 500 chars):', templateHtml.substring(0, 500));

    if (action === 'preview') {
      setPreviewHtml(templateHtml);
      setPreviewOpen(true);
    } else if (action === 'print') {
      await saveAndPrintDocument(templateHtml, format); // ✅ Use new function that saves first
    }
  };

  // ✅ NEW: Function to save ticket and then print (used when clicking print from print options)
  const saveAndPrintDocument = async (templateHtml, format) => {
    try {
      // ✅ STEP 1: Save ticket to database
      console.log('💾 Saving ticket to database...');
      
      const ticketParams = {
        cart,
        customer,
        selectedPaymentMethod,
        paidAmount,
        discount,
        customVATRate,
        receiptNumber,
        notes: `${format === 'a4' ? 'A4 Invoice' : 'Thermal Receipt'} - Printed on ${new Date().toLocaleString()}`
      };

      const ticketDto = transformCartToTicketDto(ticketParams);
      const validation = validateTicketData(ticketDto);

      if (!validation.isValid) {
        console.error('❌ Ticket validation failed:', validation.errors);
        toast.error(`Validation failed: ${validation.errors.join(', ')}`);
        return;
      }

      let createdTicket;
      try {
        createdTicket = await createTicket(ticketDto);
        console.log('✅ Ticket saved to database:', createdTicket);
        toast.success(`Ticket #${receiptNumber} saved successfully`);
      } catch (error) {
        console.error('❌ Failed to save ticket:', error);
        toast.error(`Failed to save ticket: ${error.message || 'Unknown error'}`);
        return;
      }

      // ✅ STEP 2: Print the document
      await printDocumentOnly(templateHtml, format);

      // ✅ STEP 3: Clear cart after successful print and save
      clearCart();

      console.log('✅ Save and print completed successfully');
      console.log('📊 Created Ticket ID:', createdTicket?.Id || 'N/A');
    } catch (error) {
      console.error('❌ Save and print error:', error);
      toast.error(t('print_failed') || 'Failed to print and save document');
    }
  };

  // ✅ NEW: Function to ONLY print (used when clicking print from preview dialog)
  const printDocumentOnly = async (templateHtml, format) => {
    try {
      const { jsPDF } = await import('jspdf');
      const html2canvas = (await import('html2canvas')).default;

      console.log(`🖨️ Generating ${format} PDF...`);

      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = templateHtml;
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.width = format === 'a4' ? '210mm' : '80mm';
      tempDiv.style.background = 'white';
      tempDiv.style.padding = format === 'a4' ? '20mm' : '10px';
      document.body.appendChild(tempDiv);

      const canvas = await html2canvas(tempDiv, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
      });

      document.body.removeChild(tempDiv);

      const imgData = canvas.toDataURL('image/png');
      
      let pdf;
      if (format === 'a4') {
        pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: 'a4',
        });
        const imgWidth = 210;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      } else {
        const pdfHeight = (canvas.height * 80) / canvas.width;
        pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: [80, pdfHeight],
        });
        pdf.addImage(imgData, 'PNG', 0, 0, 80, pdfHeight);
      }
      
      pdf.autoPrint();
      window.open(pdf.output('bloburl'), '_blank');
      
      console.log('✅ Document printed successfully');
      
      if (previewOpen) {
        setPreviewOpen(false);
      }
    } catch (error) {
      console.error('❌ Print error:', error);
      toast.error(t('print_failed') || 'Failed to print document');
      throw error; // Re-throw to be caught by caller
    }
  };

  // ✅ REMOVE the old printDocument function and replace with this:
// This is called from preview dialog's print button - only prints, doesn't save
const printDocument = async (templateHtml, format) => {
  await printDocumentOnly(templateHtml, format);
};

  // ✅ Updated getReceiptTemplate to use company info from Redux
  const getReceiptTemplate = () => {
    const totalExcl = cart.reduce((sum, item) => sum + item.Quantity * item.Price, 0);
    const totalVAT = cart.reduce((sum, item) => sum + (item.Quantity * item.Price * item.VAT) / 100, 0);
    const totalIncl = totalExcl + totalVAT;
    const change = (parseFloat(paidAmount) || 0) - totalIncl;

    const vatBreakdown = {};
    cart.forEach((item) => {
      const vatRate = parseFloat(item.VAT || 0);
      const itemTotal = item.Quantity * item.Price;
      const vatAmount = (itemTotal * vatRate) / 100;

      if (!vatBreakdown[vatRate]) {
        vatBreakdown[vatRate] = { base: 0, vat: 0 };
      }
      vatBreakdown[vatRate].base += itemTotal;
      vatBreakdown[vatRate].vat += vatAmount;
    });

    let vatSectionHtml = '';
    Object.keys(vatBreakdown).forEach((rate) => {
      const { base, vat } = vatBreakdown[rate];
      vatSectionHtml += `
        <tr>
          <td style="padding: 2px 0;">VAT ${rate}%:</td>
          <td style="text-align: right;">€${base.toFixed(2)}</td>
          <td style="text-align: right;">€${vat.toFixed(2)}</td>
        </tr>
      `;
    });

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: 'Courier New', monospace;
            font-size: 12px;
            line-height: 1.4;
            color: #000;
            background: white;
            padding: 10px;
          }
          .receipt-container { width: 100%; }
          .header { text-align: center; margin-bottom: 15px; border-bottom: 2px dashed #000; padding-bottom: 10px; }
          .header h1 { font-size: 18px; font-weight: 600; margin-bottom: 5px; }
          .header p { font-size: 10px; margin: 2px 0; }
          .info-section { margin: 10px 0; font-size: 11px; }
          .info-row { display: flex; justify-content: space-between; margin: 3px 0; }
          table { width: 100%; border-collapse: collapse; margin: 10px 0; }
          th { text-align: left; border-bottom: 1px solid #000; padding: 5px 0; font-size: 11px; }
          td { padding: 5px 0; font-size: 11px; }
          .item-row td { border-bottom: 1px dotted #ccc; }
          .total-section { margin-top: 15px; border-top: 2px solid #000; padding-top: 10px; }
          .total-row { display: flex; justify-content: space-between; margin: 5px 0; font-weight: 600; }
          .grand-total { font-size: 16px; margin-top: 10px; }
          .footer { text-align: center; margin-top: 20px; border-top: 2px dashed #000; padding-top: 10px; font-size: 10px; }
          .payment-info { margin: 10px 0; padding: 10px; background: #f5f5f5; border: 1px solid #ddd; }
        </style>
      </head>
      <body>
        <div class="receipt-container">
          <div class="header">
            <h1>TICKET DE CAISSE</h1>
            <p>${companyInfo?.Name || 'Your Company Name'}</p>
            <p>${companyInfo?.Address || '123 Street Address, City'}</p>
            <p>Tel: ${companyInfo?.Phone || companyInfo?.PhoneNumber || '+32 123 456 789'}</p>
            <p>TVA: ${companyInfo?.VATNumber || companyInfo?.TaxNumber || 'BE 0123.456.789'}</p>
            ${companyInfo?.Email ? `<p>Email: ${companyInfo.Email}</p>` : ''}
            ${companyInfo?.Website ? `<p>${companyInfo.Website}</p>` : ''}
          </div>

          <div class="info-section">
            <div class="info-row">
              <span>Receipt #:</span>
              <span><strong>${receiptNumber}</strong></span>
            </div>
            <div class="info-row">
              <span>Date:</span>
              <span>${new Date().toLocaleDateString('fr-BE')} ${new Date().toLocaleTimeString('fr-BE')}</span>
            </div>
            ${customer ? `
              <div class="info-row">
                <span>Client:</span>
                <span><strong>${customer.Name || 'N/A'}</strong></span>
              </div>
            ` : ''}
            <div class="info-row">
              <span>Cashier:</span>
              <span>Admin</span>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th>Product</th>
                <th style="text-align: center;">Qty</th>
                <th style="text-align: right;">Price</th>
                <th style="text-align: right;">Total</th>
              </tr>
            </thead>
            <tbody>
              ${cart.map(item => `
                <tr class="item-row">
                  <td>${item.Name || 'N/A'}</td>
                  <td style="text-align: center;">${item.Quantity}</td>
                  <td style="text-align: right;">€${item.Price.toFixed(2)}</td>
                  <td style="text-align: right;">€${(item.Quantity * item.Price).toFixed(2)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>

          <div class="total-section">
            <div class="total-row">
              <span>Subtotal (Excl. VAT):</span>
              <span>€${totalExcl.toFixed(2)}</span>
            </div>
            
            <table style="font-size: 10px; margin: 10px 0;">
              <thead>
                <tr>
                  <th>VAT Rate</th>
                  <th style="text-align: right;">Base</th>
                  <th style="text-align: right;">VAT Amount</th>
                </tr>
              </thead>
              <tbody>
                ${vatSectionHtml}
              </tbody>
            </table>

            <div class="total-row">
              <span>Total VAT:</span>
              <span>€${totalVAT.toFixed(2)}</span>
            </div>
            
            <div class="total-row grand-total">
              <span>TOTAL (Incl. VAT):</span>
              <span>€${totalIncl.toFixed(2)}</span>
            </div>
          </div>

          <div class="payment-info">
            <div class="total-row">
              <span>Payment Method:</span>
              <span>${selectedPaymentMethod}</span>
            </div>
            ${paidAmount ? `
              <div class="total-row">
                <span>Paid:</span>
                <span>€${parseFloat(paidAmount).toFixed(2)}</span>
              </div>
              <div class="total-row">
                <span>Change:</span>
                <span>€${change.toFixed(2)}</span>
              </div>
            ` : ''}
          </div>

          <div class="footer">
            <p>*** THANK YOU FOR YOUR VISIT ***</p>
            <p>Keep this receipt for exchanges and returns</p>
            ${companyInfo?.Website ? `<p>${companyInfo.Website}</p>` : '<p>www.yourcompany.com</p>'}
          </div>
        </div>
      </body>
      </html>
    `;
  };

  // ✅ Discount operations
  const handleApplyDiscount = (discountData) => {
    setDiscount(discountData);
    toast.success(
      `${t('discount_applied') || 'Discount applied'}: ${
        discountData.type === 'percentage'
          ? `${discountData.value}%`
          : `€${discountData.value.toFixed(2)}`
      }`
    );
  };

  const handleRemoveDiscount = () => {
    setDiscount(null);
    toast.info(t('discount_removed') || 'Discount removed');
  };

  // VAT handler
  const handleApplyVAT = (vatRate) => {
    if (vatRate === null || vatRate === undefined) {
      setCustomVATRate(21); // ✅ Reset to 21% instead of 0
      
      // Update all cart items to 21% VAT and recalculate base prices
      const updatedCart = cart.map(item => {
        const priceInclVAT = item.PriceInclVAT || item.Price * (1 + item.VAT / 100);
        const newBasePrice = priceInclVAT / (1 + 21 / 100);
        return {
          ...item,
          Price: newBasePrice,
          VAT: 21,
        };
      });
      setCart(updatedCart);
      
      toast.info(t('vat_reset') || 'VAT rate reset to 21%');
    } else {
      setCustomVATRate(vatRate);
      
      // Update VAT for all items and recalculate base prices
      const updatedCart = cart.map(item => {
        const priceInclVAT = item.PriceInclVAT || item.Price * (1 + item.VAT / 100);
        const newBasePrice = priceInclVAT / (1 + vatRate / 100);
        return {
          ...item,
          Price: newBasePrice,
          VAT: vatRate,
        };
      });
      setCart(updatedCart);
      
      toast.success(t('vat_applied') || `VAT rate changed to ${vatRate}%`);
    }
  };

  // ✅ Enhanced search with local-first, then DB fallback (minimum 4 characters for DB)
  const handleSearch = async (searchValue = searchQuery) => {
    if (!searchValue.trim()) {
      // If search is empty, show all local products
      setFilteredProducts(products);
      return;
    }

    const query = searchValue.toLowerCase().trim();
    
    // ✅ STEP 1: Search locally first (no minimum length requirement)
    let localResults = products.filter(p =>
      (p.Name || '').toLowerCase().includes(query) ||
      (p.SKU || '').toLowerCase().includes(query) ||
      (p.BarCode || '').toLowerCase().includes(query) ||
      (p.Description || '').toLowerCase().includes(query) ||
      (p.ProductCategory?.CategoryName || '').toLowerCase().includes(query)
    );

    console.log(`🔍 Local search for "${searchValue}":`, localResults.length, 'results');

    // ✅ If local results found, use them
    if (localResults.length > 0) {
      setFilteredProducts(localResults);
      return;
    }

    // ✅ STEP 2: Check if search query has minimum 4 characters before searching DB
    if (searchValue.length < 4) {
      console.log(`⚠️ Search query too short (${searchValue.length} chars). Need at least 4 characters for database search.`);
      setFilteredProducts([]); // Show no results for short queries with no local matches
      toast.info(t('search_min_4_chars') || 'Please enter at least 4 characters to search');
      return;
    }

    // ✅ STEP 3: No local results and query >= 4 chars, search database
    console.log(`🌐 No local results found, searching database for "${searchValue}"...`);
    setLoading(true); // ✅ Changed from setIsSearching to setLoading

    try {
      let paramsObj = {};

      // Detect search type and build paramsObj
      if (/^\d{8,14}$/.test(searchValue)) {
        // Barcode (8-14 digits)
        paramsObj = { barCode: searchValue };
        console.log('🔍 Searching by BARCODE:', searchValue);
      } else if (/^\d+$/.test(searchValue)) {
        // SKU (all digits, but not barcode length)
        paramsObj = { sku: searchValue };
        console.log('🔍 Searching by SKU:', searchValue);
      } else {
        // Name
        paramsObj = { name: searchValue };
        console.log('🔍 Searching by NAME:', searchValue);
      }

      const response = await searchProducts(paramsObj);

      let dbResults = [];
      if (Array.isArray(response)) {
        dbResults = response;
      } else if (response && response.payload && Array.isArray(response.payload)) {
        dbResults = response.payload;
      } else if (response && Array.isArray(response.data)) {
        dbResults = response.data;
      } else {
        dbResults = [];
      }

      console.log(`✅ Database search results:`, dbResults.length, 'products');

      if (dbResults.length > 0) {
        // ✅ Merge database results with local products (avoid duplicates)
        const existingIds = new Set(products.map(p => p.Id));
        const newProducts = dbResults.filter(p => !existingIds.has(p.Id));
        
        if (newProducts.length > 0) {
          console.log(`➕ Adding ${newProducts.length} new products from database to local store`);
          const updatedProducts = [...products, ...newProducts];
          setProducts(updatedProducts);
        }
        
        setFilteredProducts(dbResults);
        toast.success(`Found ${dbResults.length} product(s) in database`);
      } else {
        // ✅ No results in database either
        setFilteredProducts([]);
        toast.info(`No products found for "${searchValue}"`);
      }

    } catch (error) {
      console.error('❌ Database search error:', error);
      toast.error('Failed to search products in database');
      setFilteredProducts([]);
    } finally {
      setLoading(false); // ✅ Changed from setIsSearching to setLoading
    }
  };

  // ✅ Update useEffect for debounced search - Remove products dependency to avoid infinite loop
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      if (searchQuery !== '') {
        handleSearch(searchQuery);
      } else {
        // Show all local products when search is cleared
        setFilteredProducts(products);
      }
    }, 500); // 500ms delay for debouncing

    return () => clearTimeout(delayedSearch);
  }, [searchQuery]); // ✅ Only depend on searchQuery, not products

  // ✅ Update filterProducts to work properly with search
  useEffect(() => {
    if (!searchQuery) {
      filterProducts();
    }
  }, [selectedCategory, products]); // ✅ Only filter when category or products change, not search

  // ✅ Add toggleFullscreen function after state declarations
  const toggleFullscreen = () => {
    const elem = posContainerRef.current;
    
    if (!document.fullscreenElement && 
        !document.webkitFullscreenElement && 
        !document.mozFullScreenElement && 
        !document.msFullscreenElement) {
      // Enter fullscreen
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      } else if (elem.webkitRequestFullscreen) {
        elem.webkitRequestFullscreen();
      } else if (elem.mozRequestFullScreen) {
        elem.mozRequestFullScreen();
      } else if (elem.msRequestFullscreen) {
        elem.msRequestFullscreen();
      }
      setIsFullscreen(true);
    } else {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
      setIsFullscreen(false);
    }
  };

  return (
    <Box
      ref={posContainerRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: isFullscreen ? '100vh' : 'calc(100vh - 64px)',
        bgcolor: '#f5f7fa',
        overflow: 'hidden',
        position: isFullscreen ? 'fixed' : 'relative',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: isFullscreen ? 9999 : 'auto',
        width: '100%',
      }}
    >
      {/* Header Bar - Always Visible */}
      <Paper
        elevation={2}
        sx={{
          position: 'relative',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1100,
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          bgcolor: 'white',
          flexShrink: 0,
          borderRadius: 0,
          margin: 0,
        }}
      >
        {/* Left Section - Menu & Back Button */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isMobile && (
            <IconButton onClick={() => setCategoryDrawerOpen(true)}>
              <MenuIcon />
            </IconButton>
          )}
          {!isFullscreen && (
            <IconButton onClick={() => navigate(-1)}>
              <ArrowBackIcon />
            </IconButton>
          )}
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('point_of_sale') || 'Point of Sale'}
          </Typography>
        </Box>

        {/* Center Section - Search Bar */}
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', px: 2 }}>
          <TextField
            size="small"
            placeholder={t('search_products') || 'Search products...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: '100%',
              maxWidth: 600,
              bgcolor: 'white',
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                },
              },
            }}
          />
        </Box>

        {/* Right Section - Fullscreen Button & Receipt Number */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {/* Fullscreen Toggle Button */}
          <Tooltip title={isFullscreen ? t('exit_fullscreen') || 'Exit Fullscreen' : t('enter_fullscreen') || 'Enter Fullscreen'}>
            <IconButton
              onClick={toggleFullscreen}
              sx={{
                bgcolor: isFullscreen ? 'primary.main' : 'transparent',
                color: isFullscreen ? 'white' : 'inherit',
                '&:hover': {
                  bgcolor: isFullscreen ? 'primary.dark' : 'action.hover',
                },
              }}
            >
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Tooltip>

          {/* Receipt Number */}
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              fontWeight: 500,
              whiteSpace: 'nowrap',
            }}
          >
            {receiptNumber}
          </Typography>
        </Box>
      </Paper>

      {/* Main Content Area */}
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {/* Category Panel - Left Side */}
        {isMobile ? (
          <Drawer
            anchor="left"
            open={categoryDrawerOpen}
            onClose={() => setCategoryDrawerOpen(false)}
            container={isFullscreen ? posContainerRef.current : undefined}
            style={{ position: isFullscreen ? 'absolute' : undefined }}
          >
            <CategoryPanel
              categories={categories}
              selectedCategory={selectedCategory}
              onSelectCategory={handleCategorySelect}
            />
          </Drawer>
        ) : (
          <CategoryPanel
            categories={categories}
            selectedCategory={selectedCategory}
            onSelectCategory={handleCategorySelect}
          />
        )}

        {/* Product Grid - Center */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 2,
          }}
        >
          <ProductGrid
            products={filteredProducts}
            loading={loading}
            onSelectProduct={addToCart}
            showCustomProduct={true}
            isFullscreen={isFullscreen}
            fullscreenContainer={posContainerRef.current}
          />
        </Box>

        {/* Cart Panel - Right Side */}
        <CartPanel
          cart={cart}
          customer={customer}
          selectedPaymentMethod={selectedPaymentMethod}
          paidAmount={paidAmount}
          receiptNumber={receiptNumber}
          discount={discount}
          customVATRate={customVATRate} // ✅ Add this
          vatRates={vatRates} // ✅ Add this
          onUpdateQuantity={updateCartQuantity}
          onRemoveItem={removeFromCart}
          onClearCart={clearCart}
          onSelectCustomer={() => setCustomerDialogOpen(true)}
          onPaymentMethodChange={setSelectedPaymentMethod}
          onPaidAmountChange={setPaidAmount}
          onPreview={handlePreview}
          onPrint={handlePrint}
          onApplyDiscount={handleApplyDiscount}
          onRemoveDiscount={handleRemoveDiscount}
          onApplyVAT={handleApplyVAT} // ✅ Add this
        />

        {/* ✅ Print Options Dialog - Inside fullscreen container */}
        <PrintOptionsDialog
          open={printOptionsOpen}
          onClose={() => setPrintOptionsOpen(false)}
          onSelectFormat={handleFormatSelection}
          container={isFullscreen ? posContainerRef.current : undefined}
          style={{ position: isFullscreen ? 'absolute' : undefined }}
        />

        {/* Customer Dialog - Inside fullscreen container */}
        <CustomerDialog
          open={customerDialogOpen}
          onClose={() => setCustomerDialogOpen(false)}
          onSelectCustomer={(selectedCustomer) => {
            setCustomer(selectedCustomer);
            setCustomerDialogOpen(false);
          }}
          currentCustomer={customer}
          container={posContainerRef.current}
          style={{ position: 'absolute' }}
        />

        {/* ✅ Preview Dialogs - Inside fullscreen container */}
        {currentFormat === 'a4' ? (
          <A4PreviewDialog
            open={previewOpen}
            onClose={() => setPreviewOpen(false)}
            htmlContent={previewHtml}
            onPrint={() => printDocument(previewHtml, currentFormat)}
            container={isFullscreen ? posContainerRef.current : undefined}
            style={{ position: isFullscreen ? 'absolute' : undefined }}
          />
        ) : (
          <PreviewDialog
            open={previewOpen}
            onClose={() => setPreviewOpen(false)}
            htmlContent={previewHtml}
            onPrint={() => printDocument(previewHtml, currentFormat)}
            format="receipt"
            container={isFullscreen ? posContainerRef.current : undefined}
            style={{ position: isFullscreen ? 'absolute' : undefined }}
          />
        )}
      </Box>
    </Box>
  );
};

export default PosListPage;